import { supabase } from '@/lib/supabase';
import { projectId } from '@/utils/supabase/info';
import type { BillingOverview, BillingInvoice, BillingAccount, CashSnapshot, BillingSettings } from './types';

const BASE = `https://${projectId}.supabase.co/functions/v1/api`;

async function req<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('No active session. Please sign in.');
  const res = await fetch(`${BASE}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.error || `HTTP ${res.status}: ${res.statusText}`);
  }
  return res.json() as Promise<T>;
}

export const billingAPI = {
  getOverview: () => req<BillingOverview>('/billing/overview'),
  getInvoices: (status?: string) =>
    req<{ invoices: BillingInvoice[] }>(`/billing/invoices${status ? `?status=${status}` : ''}`),
  getAccounts: () => req<{ accounts: BillingAccount[] }>('/billing/accounts'),
  getCash: () => req<{ snapshots: CashSnapshot[] }>('/billing/cash'),
  getSettings: () => req<{ settings: BillingSettings }>('/billing/settings'),

  async downloadInvoicesCsv(): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('No active session. Please sign in.');
    const res = await fetch(`${BASE}/billing/export.csv`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (!res.ok) throw new Error(`Export failed: HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'xyz-invoices.csv';
    a.click();
    URL.revokeObjectURL(url);
  },
};
